import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, Briefcase, FileCheck2, Sparkles, Compass, UserRound, Lock, CreditCard, Building2, Users, ShieldAlert, Activity, LogOut, Menu, X, Clock3, FileSearch } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import VerifyBanner from "@/components/VerifyBanner";
import { UserAvatar } from "@/components/UserAvatar";

const PLAN_NAMES = { cand_free: "Free", cand_professional: "Professional", cand_careerpro: "Career Pro", cand_executive: "Executive Elite",
  rec_free: "No plan", rec_starter: "Starter", rec_growth: "Growth", rec_agency: "Agency Pro", rec_enterprise: "Enterprise" };
const CANDIDATE = [
  ["/app", "Dashboard", LayoutDashboard], ["/app/jobs", "Job discovery", Briefcase], ["/app/resume", "Resume check", FileSearch], ["/app/hourly", "Hourly & shift jobs", Clock3], ["/app/applications", "Applications", FileCheck2],
  ["/app/agents", "JobPedestal Intelligence", Sparkles], ["/app/autopilot", "Career Autopilot", Compass], ["/app/profile", "Profile", UserRound],
];
const RECRUITER = [["/recruiter", "Employer portal", Building2], ["/recruiter/talent", "Talent pool", Users]];
const COMMON = [["/app/billing", "Plan & billing", CreditCard], ["/app/privacy", "Privacy center", Lock], ["/status", "System status", Activity]];

function navFor(role) {
  if (role === "recruiter") return [...RECRUITER, ...COMMON];
  if (role === "admin") return [["/admin", "Admin console", ShieldAlert], ...CANDIDATE, ...RECRUITER, ...COMMON];
  return [...CANDIDATE, ...COMMON];
}

const NavItems = ({ role, onClick }) => navFor(role).map(([to, text, Icon]) => (
  <NavLink key={to} to={to} end onClick={onClick} data-testid={`nav-${text.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
    <Icon className="h-4 w-4" aria-hidden /> {text}
  </NavLink>
));

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/login"); };
  const sidebar = (
    <div className="flex h-full flex-col bg-[hsl(var(--obsidian))] p-5 text-white">
      <div className="mb-8 flex items-center gap-2.5 font-display text-lg font-extrabold tracking-wide" data-testid="sidebar-brand"><img src="/logo-icon.png" alt="" className="h-9 w-9 rounded-lg ring-1 ring-white/10" />JobPedestal</div>
      <nav className="flex-1 space-y-1 overflow-y-auto" aria-label="Main"><NavItems role={user.role} onClick={() => setOpen(false)} /></nav>
      <div className="mt-6 border-t border-white/10 pt-4">
        <div className="flex items-center gap-3">
          <UserAvatar user={user} testid="shell-user-avatar" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium" data-testid="shell-user-name">{user.name || user.email}</p>
            <p className="text-xs capitalize text-slate-400" data-testid="shell-user-role">{user.role} · {PLAN_NAMES[user.plan] || "Free"}</p>
          </div>
        </div>
        <button onClick={doLogout} data-testid="logout-button" className="mt-3 flex items-center gap-2 text-sm text-slate-400 hover:text-white"><LogOut className="h-4 w-4" /> Sign out</button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:block lg:sticky lg:top-0 lg:h-screen">{sidebar}</aside>
      <div className="sticky top-0 z-40 flex items-center justify-between border-b bg-white/80 px-4 py-3 backdrop-blur-xl lg:hidden">
        <img src="/logo-full.png" alt="JobPedestal" className="h-7 w-auto" data-testid="mobile-brand-logo" />
        <button data-testid="mobile-nav-toggle" aria-label="Open navigation" onClick={() => setOpen(true)}><Menu /></button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72">{sidebar}<button aria-label="Close" className="absolute right-3 top-4 text-white" onClick={() => setOpen(false)}><X /></button></div>
        </div>
      )}
      <main className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-8 lg:py-12"><VerifyBanner /><Outlet /></main>
    </div>
  );
}
